import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
    Table,
    Button,
    Space,
    Typography,
    Divider,
    message,
    Modal,
    Form,
    Input,
    Drawer,
    Tabs,
    Empty,
    Breadcrumb
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    ExclamationCircleFilled,
    ArrowLeftOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

// TODO: 联调时替换为接口数据
const MOCK_RECOMMEND_POSITIONS = [
    { id: '1', name: '首页推荐位', code: 'rec_home_001', createTime: '2026-01-15 10:30:00' },
    { id: '2', name: '设备详情推荐位', code: 'rec_device_001', createTime: '2026-02-20 14:20:00' },
    { id: '3', name: '场景推荐位', code: 'rec_scene_top', createTime: '2026-03-10 09:15:00' },
];

const OperationConfig = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const appName = location.state?.appName || '应用';

    const [activeTab, setActiveTab] = useState('recommend');
    const [dataSource, setDataSource] = useState(MOCK_RECOMMEND_POSITIONS);
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);
    const [form] = Form.useForm();

    // 打开新增抽屉
    const handleAdd = () => {
        setEditingRecord(null);
        form.resetFields();
        setDrawerVisible(true);
    };

    // 打开编辑抽屉
    const handleEdit = (record) => {
        setEditingRecord(record);
        form.setFieldsValue({
            name: record.name,
            code: record.code,
        });
        setDrawerVisible(true);
    };

    // 删除
    const handleDelete = (record) => {
        Modal.confirm({
            title: '删除推荐位',
            icon: <ExclamationCircleFilled />,
            content: (
                <div>
                    <div style={{ marginBottom: 12 }}>
                        你正在删除推荐位 <Text strong>【{record.name}】</Text>（编码：{record.code}），
                    </div>
                    <div style={{ color: '#ff4d4f', fontWeight: 500 }}>
                        删除后不可恢复，确认继续删除操作吗？
                    </div>
                </div>
            ),
            okText: '确认删除',
            okButtonProps: { danger: true },
            cancelText: '取消',
            onOk() {
                setDataSource(prev => prev.filter(item => item.id !== record.id));
                message.success('推荐位已删除');
            },
        });
    };

    // 保存（新增 / 编辑）
    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            if (editingRecord) {
                // 编辑
                setDataSource(prev =>
                    prev.map(item =>
                        item.id === editingRecord.id
                            ? { ...item, name: values.name, code: values.code }
                            : item
                    )
                );
                message.success('推荐位已更新');
            } else {
                // 新增
                const newRecord = {
                    id: String(Date.now()),
                    name: values.name,
                    code: values.code,
                    createTime: new Date().toLocaleString('zh-CN', { hour12: false }).replace(/\//g, '-'),
                };
                setDataSource(prev => [newRecord, ...prev]);
                message.success('推荐位已添加');
            }
            setDrawerVisible(false);
            form.resetFields();
            setEditingRecord(null);
        } catch (e) {
            // 表单校验失败
        }
    };

    // 关闭抽屉
    const handleCloseDrawer = () => {
        setDrawerVisible(false);
        form.resetFields();
        setEditingRecord(null);
    };

    const columns = [
        {
            title: '推荐位名称',
            dataIndex: 'name',
            key: 'name',
            width: 200,
        },
        {
            title: '推荐位编码',
            dataIndex: 'code',
            key: 'code',
            width: 220,
            render: (text) => <Text code>{text}</Text>,
        },
        {
            title: '创建时间',
            dataIndex: 'createTime',
            key: 'createTime',
            width: 200,
        },
        {
            title: '操作',
            key: 'action',
            width: 150,
            render: (_, record) => (
                <Space size={0} split={<Divider type="vertical" style={{ margin: '0 4px' }} />}>
                    <Button
                        type="link"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                    >
                        编辑
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record)}
                    >
                        删除
                    </Button>
                </Space>
            ),
        },
    ];

    const tabItems = [
        {
            key: 'advert',
            label: '广告位配置',
            children: (
                <div style={{ padding: '80px 0', textAlign: 'center' }}>
                    <Empty description="暂无内容" />
                </div>
            ),
        },
        {
            key: 'recommend',
            label: '推荐位配置',
            children: (
                <div>
                    <Table
                        columns={columns}
                        dataSource={dataSource}
                        rowKey="id"
                        pagination={{
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total) => `共 ${total} 条记录`,
                            defaultPageSize: 10,
                        }}
                        style={{ border: '1px solid #f0f0f0', borderRadius: '8px' }}
                    />
                </div>
            ),
        },
    ];

    return (
        <div style={{ padding: '24px', backgroundColor: '#fff', minHeight: '100vh' }}>
            {/* 面包屑导航 */}
            <Breadcrumb
                style={{ marginBottom: 16 }}
                items={[
                    {
                        title: (
                            <Button
                                type="link"
                                size="small"
                                icon={<ArrowLeftOutlined />}
                                style={{ padding: 0 }}
                                onClick={() => navigate('/app/manage')}
                            >
                                应用管理
                            </Button>
                        ),
                    },
                    { title: `${appName} 运营配置` },
                ]}
            />

            {/* 页面标题 */}
            <div style={{ marginBottom: 24 }}>
                <Title level={4} style={{ margin: 0 }}>
                    {appName} · 运营配置
                </Title>
            </div>

            {/* Tab 切换 */}
            <Tabs
                activeKey={activeTab}
                onChange={setActiveTab}
                items={tabItems}
                tabBarExtraContent={
                    activeTab === 'recommend' && (
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={handleAdd}
                        >
                            添加推荐位
                        </Button>
                    )
                }
            />

            {/* 新增 / 编辑推荐位抽屉 */}
            <Drawer
                title={editingRecord ? '编辑推荐位' : '添加推荐位'}
                width={520}
                onClose={handleCloseDrawer}
                open={drawerVisible}
                destroyOnClose
                footer={
                    <div style={{ textAlign: 'right', padding: '10px 16px' }}>
                        <Space>
                            <Button onClick={handleCloseDrawer}>取消</Button>
                            <Button type="primary" onClick={handleSave}>
                                保存
                            </Button>
                        </Space>
                    </div>
                }
            >
                <Form
                    form={form}
                    layout="vertical"
                    style={{ marginTop: 8 }}
                >
                    <Form.Item
                        label="推荐位名称"
                        name="name"
                        rules={[{ required: true, message: '请输入推荐位名称' }]}
                    >
                        <Input placeholder="请输入推荐位名称" maxLength={30} showCount />
                    </Form.Item>

                    <Form.Item
                        label="推荐位编码"
                        name="code"
                        rules={[
                            { required: true, message: '请输入推荐位编码' },
                            { pattern: /^[a-zA-Z][a-zA-Z0-9_]*$/, message: '编码仅支持字母、数字、下划线，必须以字母开头' },
                        ]}
                        extra={
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                建议使用英文+下划线格式，如 rec_home_001；编码确定后如需修改请与APP端同步
                            </Text>
                        }
                    >
                        <Input placeholder="请输入推荐位编码，如 rec_home_001" maxLength={50} />
                    </Form.Item>
                </Form>
            </Drawer>
        </div>
    );
};

export default OperationConfig;
